import { diffWords } from "diff";
import { DiffPart } from "./markdownDiffTypes";

export const containsOnlySpecialCharsAndPunctuation = (
  text: string
): boolean => {
  const trimmed = text.trim();
  if (trimmed === "") {
    return true;
  }

  return /^[\s\W]+$/.test(trimmed);
};

export const wrapContentInSpan = (
  content: string,
  added: boolean,
  removed: boolean
): string => {
  const trimmed = content.trim();
  if (trimmed === "") return content;

  if (!added && !removed) return content;

  const trailingSpacesMatch = content.match(/( {2,})$/);
  if (trailingSpacesMatch) {
    const trailingSpaces = trailingSpacesMatch[1];
    const contentWithoutTrailing = content.slice(0, -trailingSpaces.length);
    const className = added ? "added" : "removed";
    return `<span class="${className}">${contentWithoutTrailing}</span>${trailingSpaces}`;
  }

  if (added) return `<span class="added">${content}</span>`;
  if (removed) return `<span class="removed">${content}</span>`;
  return content;
};

export const renderLeafTextDiff = (
  previousValue: string,
  currentValue: string,
  options?: {
    wrapSpecialCharacters?: boolean;
  }
): string => {
  const wrapSpecialCharacters = options?.wrapSpecialCharacters ?? true;

  if (!previousValue) {
    return wrapInlineDiffPart(currentValue, true, false, wrapSpecialCharacters);
  }

  if (!currentValue) {
    return wrapInlineDiffPart(
      previousValue,
      false,
      true,
      wrapSpecialCharacters
    );
  }

  if (previousValue === currentValue) {
    return currentValue;
  }

  return diffWords(previousValue, currentValue)
    .map((part) =>
      wrapInlineDiffPart(
        part.value,
        Boolean(part.added),
        Boolean(part.removed),
        wrapSpecialCharacters
      )
    )
    .join("");
};

export const renderTextBlockDiff = (
  previousContent: string,
  currentContent: string
): string => {
  if (previousContent === currentContent) {
    return currentContent;
  }

  const diff = diffWords(previousContent, currentContent);
  const lines = separateDiffInLines(diff);
  let result = "";

  lines.forEach((line) => {
    const value = line.value;
    const added = Boolean(line.added);
    const removed = Boolean(line.removed);

    if (value === "") {
      result += "\n";
      return;
    }

    const trimmedValue = value.trim();

    if (containsOnlySpecialCharsAndPunctuation(value)) {
      result += value;
      return;
    }

    const headingMatch = trimmedValue.match(/^(#+)\s+(.+)$/);
    if (headingMatch) {
      result += `${headingMatch[1]} ${wrapContentInSpan(
        headingMatch[2],
        added,
        removed
      )}`;
      return;
    }

    const listMatch = trimmedValue.match(/^([-*+])\s+(.+)$/);
    if (listMatch) {
      result += `${listMatch[1]} ${wrapContentInSpan(
        listMatch[2],
        added,
        removed
      )}`;
      return;
    }

    const numberedListMatch = trimmedValue.match(/^(\d+\.)\s+(.+)$/);
    if (numberedListMatch) {
      result += `${numberedListMatch[1]} ${wrapContentInSpan(
        numberedListMatch[2],
        added,
        removed
      )}`;
      return;
    }

    const blockquoteMatch = trimmedValue.match(/^(>)\s*(.*)$/);
    if (blockquoteMatch) {
      result += `${blockquoteMatch[1]} ${wrapContentInSpan(
        blockquoteMatch[2] || "",
        added,
        removed
      )}`;
      return;
    }

    result += wrapContentInSpan(value, added, removed);
  });

  return result;
};

const wrapInlineDiffPart = (
  value: string,
  added: boolean,
  removed: boolean,
  wrapSpecialCharacters: boolean
): string => {
  if (!added && !removed) {
    return value;
  }

  if (!wrapSpecialCharacters && containsOnlySpecialCharsAndPunctuation(value)) {
    return value;
  }

  return wrapContentInSpan(value, added, removed);
};

const separateDiffInLines = (parts: Array<DiffPart>): Array<DiffPart> => {
  return parts.flatMap((part) => {
    if (!part.value.includes("\n")) {
      return [part];
    }

    const hardBreakPlaceholder = "\x00HARDBREAK\x00";
    const withPlaceholders = part.value.replace(/  \n/g, hardBreakPlaceholder);
    const splitLines = withPlaceholders.split("\n");
    const endsWithNewline = part.value.endsWith("\n");
    const newParts: Array<DiffPart> = [];

    for (let index = 0; index < splitLines.length; index += 1) {
      newParts.push({
        value: splitLines[index].split(hardBreakPlaceholder).join("  \n"),
        added: part.added,
        removed: part.removed,
      });

      const nextIsTrailingEmpty =
        index === splitLines.length - 2 &&
        endsWithNewline &&
        splitLines[index + 1] === "";

      if (index < splitLines.length - 1 && !nextIsTrailingEmpty) {
        newParts.push({
          value: "",
          added: part.added,
          removed: part.removed,
        });
      }
    }

    return newParts;
  });
};
